import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export const MessageContext = createContext({
  contextProviders: [],
  registerContextProvider: () => {},
  unregisterContextProvider: () => {},
  getFormattedMessage: () => {},
  getActiveContextDescription: () => null,
  hasActiveContext: false
});

export const MessageContextProvider = ({ children }) => {
  const [contextProviders, setContextProviders] = useState([]);

  const unregisterContextProvider = useCallback((type) => {
    setContextProviders(prev => prev.filter(provider => provider.type !== type));
  }, []);

  const registerContextProvider = useCallback((provider) => {
    setContextProviders(prev => {
      const existingIndex = prev.findIndex(p => p.type === provider.type);
      let newProviders;
      if (existingIndex >= 0) {
        newProviders = [...prev];
        newProviders[existingIndex] = provider;
      } else {
        newProviders = [...prev, provider];
      }
      // Sort by priority (higher priority first)
      return newProviders.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    });
    return () => unregisterContextProvider(provider.type);
  }, [unregisterContextProvider]);


  const getFormattedMessage = useCallback((originalMessage) => {
    const activeProviders = contextProviders.filter(provider => provider.isActive());

    if (activeProviders.length === 0) {
      return originalMessage;
    }

    // The list is already sorted by priority (descending) due to registerContextProvider
    const primaryContextProvider = activeProviders[0]; // Highest priority
    const contextValue = primaryContextProvider.getContextValue();

    if (contextValue && typeof contextValue === 'string' && contextValue.trim() !== '') {
      return `${contextValue} ${originalMessage}`;
    }
    
    return originalMessage;

  }, [contextProviders]);

  const getActiveContextDescription = useCallback(() => {
    const activeProviders = contextProviders.filter(provider => provider.isActive());
    if (activeProviders.length === 0) {
      return null;
    }
    return activeProviders.map(provider => provider.getDescription()).filter(Boolean);
  }, [contextProviders]);

  const hasActiveContext = useCallback(() => {
    return contextProviders.some(provider => provider.isActive() && provider.getContextValue());
  }, [contextProviders]);

  const contextValue = useMemo(() => ({
    contextProviders,
    registerContextProvider,
    unregisterContextProvider,
    getFormattedMessage,
    getActiveContextDescription,
    hasActiveContext
  }), [
    contextProviders,
    registerContextProvider,
    unregisterContextProvider,
    getFormattedMessage,
    getActiveContextDescription,
    hasActiveContext
  ]);

  return (
    <MessageContext.Provider value={contextValue}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessageContext = () => useContext(MessageContext);

export const createContextProvider = (type, config) => {
  return {
    type,
    isActive: config.isActive,
    getContextValue: config.getContextValue,
    getDescription: config.getDescription || (() => null),
    icon: config.icon || null,
    priority: config.priority || 0
  };
};

export default MessageContext;